import { Link } from 'react-router-dom';
import { Callout, DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'compatibility', label: 'Compatibility' },
  { id: 'install', label: 'Install the panel' },
  { id: 'open', label: 'Open it' },
  { id: 'what-it-can-do', label: 'What the panel can do' },
  { id: 'placement-modes', label: 'Placement modes' },
  { id: 'updating', label: 'Keeping the panel updated' },
];

export default function Body() {
  return (
    <>
      <DocSection id="compatibility" title="Compatibility">
        <DocShot
          slug="premiere-panel"
          shot="panel-timeline"
          alt="The ClipRadar panel docked in Premiere Pro beside an open sequence, with search results ready to place"
          caption="The panel is the same ClipRadar app you'd open standalone, docked inside Premiere Pro."
        />
        <p>
          The <Link to="/docs/platforms/standalone-app">desktop app</Link> is what the Premiere panel runs on
          top of, so it inherits the same requirements:
        </p>
        <ul>
          <li><strong>Windows 10/11, 64-bit</strong> — same as the desktop app.</li>
          <li>
            An <strong>NVIDIA GPU (CUDA) is required</strong>. The panel doesn't index or search on its own —
            it talks to the desktop app's local server, so it needs the same GPU the desktop app needs.
          </li>
          <li><strong>Adobe Premiere Pro 22.3 or later.</strong> Older versions can't load the panel.</li>
        </ul>
        <p>
          For exact VRAM/RAM/disk figures, see <Link to="/docs/reference/system-requirements">System
          requirements</Link>.
        </p>
      </DocSection>

      <DocSection id="install" title="Install the panel">
        <p>
          The panel installs from a small <span style={kbd}>.ccx</span> file the desktop app already has —
          nothing to download separately.
        </p>
        <ol>
          <li>
            In ClipRadar, open <span style={kbd}>Settings</span> → <span style={kbd}>Install Premiere panel</span>.
          </li>
          <li>
            Reveal the <span style={kbd}>.ccx</span> file in File Explorer, then double-click it. Windows hands
            it to Creative Cloud's installer, which registers the panel with Premiere Pro.
          </li>
          <li>
            If double-clicking doesn't do anything — some machines don't have Creative Cloud wired up to{' '}
            <span style={kbd}>.ccx</span> files — install it instead with the free{' '}
            <a href="https://zxpinstaller.com/" target="_blank" rel="noopener noreferrer">
              ZXP/UXP Installer
            </a>.
          </li>
        </ol>
        <DocShot
          slug="premiere-panel"
          shot="install-setting"
          alt="Settings screen showing the Install Premiere panel section"
          caption="Settings → Install Premiere panel reveals the .ccx file for you."
          callouts={[
            { n: 1, label: 'Install Premiere panel section, in Settings' },
            { n: 2, label: 'Reveal the .ccx file in File Explorer' },
          ]}
        />
        <DocShot
          slug="premiere-panel"
          shot="ccx-explorer"
          alt="The ClipRadar .ccx file selected in File Explorer, ready to double-click"
          caption="Double-click the .ccx to install it via Creative Cloud."
        />
      </DocSection>

      <DocSection id="open" title="Open it">
        <p>
          Start the ClipRadar desktop app first — even just{' '}
          <Link to="/docs/platforms/standalone-app">running in the tray</Link> is enough. The panel doesn't
          run its own copy of ClipRadar; it connects to the desktop app's local server, so that server has to
          be up before the panel has anything to show.
        </p>
        <p>
          In Premiere Pro: <span style={kbd}>Window → ClipRadar</span>. If you opened the panel before
          starting ClipRadar, or the desktop app restarted while the panel was open, use the panel's{' '}
          <span style={kbd}>Reconnect</span> control rather than reopening it from the Window menu.
        </p>
        <Callout>
          The panel's default docked size is small. Drag its edge wider, or drag it out of the dock into a
          floating window, for a more comfortable search view.
        </Callout>
        <p>
          Still not connecting? See{' '}
          <Link to="/docs/troubleshooting/panel-wont-open">The Premiere panel won't open or is blank</Link>.
        </p>
        <DocShot
          slug="premiere-panel"
          shot="window-menu"
          alt="Premiere Pro's Window menu open with ClipRadar in the list"
          caption="Window → ClipRadar opens the panel once the desktop app is running."
        />
      </DocSection>

      <DocSection id="what-it-can-do" title="What the panel can do">
        <p>
          <Link to="/docs/interface/search">Search</Link> works exactly like the desktop app — the difference
          is what each result card does. In the panel, video result cards place clips straight into your
          sequence instead of exporting a file:
        </p>
        <DocShot
          slug="premiere-panel"
          shot="card-buttons"
          alt="A panel result card's row of five action buttons"
          caption="The panel's five card actions, in order."
          panel
          callouts={[
            { n: 1, label: 'Add to timeline (video + audio)' },
            { n: 2, label: 'Add to timeline (video only) — e.g. over your own VO' },
            { n: 3, label: 'Open in Source Monitor — in/out already set from the match' },
            { n: 4, label: 'Open in Explorer — reveals the source file on disk' },
            { n: 5, label: 'Find similar — a new visual search seeded from this clip' },
          ]}
        />
        <p>
          Speech result cards show a shorter row of three: <span style={kbd}>Add to timeline</span>,{' '}
          <span style={kbd}>Open in Source Monitor</span>, and <span style={kbd}>Open transcript</span>. Image
          result cards show <span style={kbd}>Find similar</span> and <span style={kbd}>Open in Explorer</span>.
        </p>
        <p>
          Two more ways to search live from Premiere: click the camera button next to the search box to grab
          the current <strong>Program monitor</strong> frame as a search image, or copy any image and press{' '}
          <span style={kbd}>Ctrl+V</span> in the search box to search by it.
        </p>
        <p>
          If a clip's source file has moved, right-click its result card and choose{' '}
          <span style={kbd}>Relink missing file…</span> to point ClipRadar at the new location — the same
          option exists on rows in the <Link to="/docs/interface/media">Media</Link> tab.
        </p>
      </DocSection>

      <DocSection id="placement-modes" title="Placement modes">
        <p>
          Every <span style={kbd}>Add to timeline</span> button uses whichever placement mode is currently
          selected in the panel's toolbar:
        </p>
        <ul>
          <li><span style={kbd}>Smart</span> — adds the clip to an open track without disturbing anything already on the timeline. Non-destructive, and the default.</li>
          <li><span style={kbd}>Insert</span> — ripples everything at the playhead to make room, like a manual insert edit.</li>
          <li><span style={kbd}>Append</span> — adds the clip to the end of the sequence.</li>
        </ul>
        <p>
          Set the mode once and it applies to every <span style={kbd}>Add to timeline</span> click until you
          change it. See{' '}
          <Link to="/docs/guides/build-a-rough-cut">From search results to a rough cut</Link> for a worked
          example of placing a run of clips.
        </p>
      </DocSection>

      <DocSection id="updating" title="Keeping the panel updated">
        <p>
          The panel doesn't update itself separately — its UI is served live by the desktop app, so a normal
          ClipRadar auto-update carries the panel along with it automatically, the next time you open it in
          Premiere. Nothing to reinstall for most releases.
        </p>
        <p>
          The one exception is a change to the panel <em>shell</em> itself — the thin wrapper that hosts the
          webview, not the app inside it. That's rare; when it happens, install the new{' '}
          <span style={kbd}>.ccx</span> the same way as above.
        </p>
      </DocSection>
    </>
  );
}
